import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { MessageSquare, Loader2, Send } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import LogRocket from 'logrocket';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage: string;
  currentView?: string;
  giftGroupId?: string | null;
  giftGroupTitle?: string;
}

export function FeedbackDialog({
  isOpen,
  onClose,
  currentPage,
  currentView,
  giftGroupId,
  giftGroupTitle,
}: FeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await api.submitFeedback({
        feedback: feedback.trim(),
        currentPage,
        currentView,
        giftGroupId: giftGroupId || undefined,
        giftGroupTitle,
      });

      if (result.success) {
        // Log to LogRocket that feedback was provided
        console.log('[Feedback] User submitted feedback', {
          feedbackId: result.feedbackId,
          currentPage,
          currentView,
          giftGroupId,
          feedbackLength: feedback.trim().length,
        });

        // Track as a LogRocket event for potential issue tracking
        try {
          LogRocket.track('Feedback Submitted', {
            feedbackId: result.feedbackId,
            currentPage,
            currentView: currentView || 'none',
            giftGroupId: giftGroupId || 'none',
            feedbackLength: feedback.trim().length,
          });
          console.log('[LogRocket] Feedback event tracked');
        } catch (logRocketError) {
          console.error('[LogRocket] Failed to track feedback event:', logRocketError);
        }

        toast.success('Thank you for your feedback!', {
          description: 'We appreciate you helping us improve.',
        });

        setFeedback('');
        onClose();
      } else {
        toast.error('Failed to submit feedback', {
          description: 'Please try again.',
        });
      }
    } catch (error) {
      console.error('[Feedback] Error submitting feedback:', error);
      toast.error('Failed to submit feedback', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <MessageSquare className="w-5 h-5" />
            Share Your Feedback
          </DialogTitle>
          <DialogDescription className="text-left">
            We are brand new, born November 28th, 2025 and would love to know how we can make this service better for you so let us have it. ;)
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Textarea
            placeholder="Tell us what you think..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="min-h-[150px] resize-none"
            maxLength={5000}
            disabled={isSubmitting}
          />
          <div className="text-xs text-muted-foreground mt-2 text-right">
            {feedback.length}/5000 characters
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !feedback.trim()}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
